import { useMutation, useQueryClient } from '@tanstack/react-query';

import { fetcher, throwApiError } from '@commenda/utils';

import { scheduledAgentsKeys } from './keys';
import { SCHEDULED_AGENTS_ROUTE } from './routes';
import type {
  CreateScheduledAgentParams,
  IMySubscription,
  IScheduledAgent,
  IScheduledAgentDetailResponse,
  UpdateMySubscriptionParams,
  UpdateScheduledAgentParams,
} from './types';

export function useCreateScheduledAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: CreateScheduledAgentParams) => {
      const res = await fetcher(SCHEDULED_AGENTS_ROUTE, {
        method: 'POST',
        body: params,
      });
      if (!res.ok) await throwApiError(res, 'Failed to create the agent');
      return ((await res.json()) as { agent: IScheduledAgent }).agent;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scheduledAgentsKeys.lists() });
    },
  });
}

export function useInstantiateManagedAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      key: string;
      companyId: number;
      timezone: string;
      scopeConfig?: { entityIds?: number[] };
    }) => {
      const res = await fetcher(
        `${SCHEDULED_AGENTS_ROUTE}/managed-agents/${params.key}/instantiate`,
        {
          method: 'POST',
          body: {
            companyId: params.companyId,
            timezone: params.timezone,
            scopeConfig: params.scopeConfig,
          },
        }
      );
      if (!res.ok) await throwApiError(res, 'Failed to add the managed agent');
      return ((await res.json()) as { agent: IScheduledAgent }).agent;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scheduledAgentsKeys.lists() });
    },
  });
}

export function useUpdateScheduledAgent(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: UpdateScheduledAgentParams) => {
      const res = await fetcher(`${SCHEDULED_AGENTS_ROUTE}/${agentId}`, {
        method: 'PATCH',
        body: params,
      });
      if (!res.ok) await throwApiError(res, 'Failed to update the agent');
      return ((await res.json()) as { agent: IScheduledAgent }).agent;
    },
    onSuccess: (agent) => {
      qc.setQueryData<IScheduledAgentDetailResponse>(
        scheduledAgentsKeys.detail(agentId),
        (previous) => (previous ? { ...previous, agent } : previous)
      );
      qc.invalidateQueries({ queryKey: scheduledAgentsKeys.lists() });
    },
  });
}

export function useUpdateMySubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      agentId,
      ...params
    }: UpdateMySubscriptionParams & { agentId: string }) => {
      const res = await fetcher(
        `${SCHEDULED_AGENTS_ROUTE}/${agentId}/subscription`,
        { method: 'PUT', body: params }
      );
      if (!res.ok)
        await throwApiError(res, 'Failed to update your subscription');
      return ((await res.json()) as { subscription: IMySubscription })
        .subscription;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scheduledAgentsKeys.all });
    },
  });
}

export function useMarkRunRead(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string) => {
      const res = await fetcher(
        `${SCHEDULED_AGENTS_ROUTE}/${agentId}/runs/${conversationId}/read`,
        { method: 'POST', body: {} }
      );
      if (!res.ok) await throwApiError(res, 'Failed to mark the run read');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scheduledAgentsKeys.runs(agentId) });
      qc.invalidateQueries({ queryKey: scheduledAgentsKeys.lists() });
    },
  });
}

export function useRunScheduledAgentNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (agentId: string) => {
      const res = await fetcher(`${SCHEDULED_AGENTS_ROUTE}/${agentId}/run`, {
        method: 'POST',
        body: {},
      });
      if (!res.ok) await throwApiError(res, 'Failed to start the run');
      return (await res.json()) as { taskId: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scheduledAgentsKeys.all });
    },
  });
}

export function usePauseScheduledAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (agentId: string) => {
      const res = await fetcher(`${SCHEDULED_AGENTS_ROUTE}/${agentId}/pause`, {
        method: 'POST',
        body: {},
      });
      if (!res.ok) await throwApiError(res, 'Failed to pause the agent');
      return (await res.json()) as { success: true };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scheduledAgentsKeys.all });
    },
  });
}

export function useResumeScheduledAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (agentId: string) => {
      const res = await fetcher(`${SCHEDULED_AGENTS_ROUTE}/${agentId}/resume`, {
        method: 'POST',
        body: {},
      });
      if (!res.ok) await throwApiError(res, 'Failed to resume the agent');
      return (await res.json()) as { success: true };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scheduledAgentsKeys.all });
    },
  });
}

export function useDeleteScheduledAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (agentId: string) => {
      const res = await fetcher(`${SCHEDULED_AGENTS_ROUTE}/${agentId}`, {
        method: 'DELETE',
      });
      if (!res.ok) await throwApiError(res, 'Failed to delete the agent');
      return (await res.json().catch(() => ({ success: true }))) as {
        success: true;
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scheduledAgentsKeys.lists() });
    },
  });
}
